import React from 'react';
import {
  Dialog, DialogContent, Box, Typography, IconButton, Divider, Chip,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined';
import TagOutlinedIcon from '@mui/icons-material/TagOutlined';

function getInitials(title) {
  if (!title) return 'RYF';
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 'RYFc';
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

const STATUS_COLORS = {
  Open:     { bg: '#FEE2E2', color: '#DC2626' },
  Closed:   { bg: '#D1FAE5', color: '#059669' },
  Reported: { bg: '#FEF3C7', color: '#D97706' },
};

function MetaRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: '10px', flexShrink: 0, mt: 0.1,
        background: '#F7F8FA',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 15, color: '#9CA3AF' } })}
      </Box>
      <Box>
        <Typography sx={{ fontFamily: 'Heebo', fontSize: 11, color: '#9CA3AF', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.2, mb: 0.3 }}>
          {label}
        </Typography>
        <Typography sx={{ fontFamily: 'Heebo', fontSize: 13, color: '#362B24', fontWeight: 500, lineHeight: 1.4 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

const TicketDetailsModal = ({ open, handleClose, selectedTicket }) => {
  const cfg = STATUS_COLORS[selectedTicket?.status] || { bg: '#F3F4F6', color: '#6B7280' };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
        },
      }}
    >
      {/* green accent bar */}
      <Box sx={{ height: 4, background: 'linear-gradient(90deg, #065F46 0%, #0D7B5C 100%)' }} />

      {/* header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Box sx={{
            width: 46, height: 46, borderRadius: '14px', flexShrink: 0,
            background: 'linear-gradient(135deg, #065F46 0%, #0D7B5C 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 15, fontWeight: 700, fontFamily: 'Heebo',
          }}>
            {getInitials(selectedTicket?.title)}
          </Box>
          <Box>
            <Typography sx={{ fontFamily: 'Heebo', fontWeight: 700, fontSize: 15, color: '#362B24', lineHeight: 1.3 }}>
              {selectedTicket?.title || 'N/A'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4 }}>
              <Typography sx={{ fontFamily: 'Heebo', fontSize: 11, color: '#9CA3AF' }}>
                #{selectedTicket?.ticketNumber || '—'}
              </Typography>
              <Box sx={{
                px: 1.25, borderRadius: '20px', lineHeight: 1.9,
                background: cfg.bg, color: cfg.color,
                fontSize: 10, fontWeight: 700, fontFamily: 'Heebo',
              }}>
                {selectedTicket?.status || 'N/A'}
              </Box>
            </Box>
          </Box>
        </Box>

        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ color: '#C4C9D4', mt: 0.25, '&:hover': { color: '#362B24', background: '#F7F8FA' } }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: '#F5F5F5' }} />

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <MetaRow
            icon={<LabelOutlinedIcon />}
            label="Title"
            value={selectedTicket?.title}
          />
          <MetaRow
            icon={<SubjectOutlinedIcon />}
            label="Topic"
            value={selectedTicket?.subject}
          />
          <MetaRow
            icon={<CalendarTodayOutlinedIcon />}
            label="Created On"
            value={
              selectedTicket?.createdAt
                ? new Date(selectedTicket.createdAt).toLocaleDateString('en-IN', {
                    year: 'numeric', month: 'long', day: 'numeric',
                  })
                : null
            }
          />

          {selectedTicket?.description && (
            <Box>
              <Typography sx={{ fontFamily: 'Heebo', fontSize: 11, color: '#9CA3AF', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                Description
              </Typography>
              <Box sx={{ p: 1.5, background: '#F7F8FA', borderRadius: '12px', border: '1px solid #F0F0F0' }}>
                <Typography sx={{ fontFamily: 'Heebo', fontSize: 13, color: '#362B24', lineHeight: 1.6 }}>
                  {selectedTicket.description}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TicketDetailsModal;
